/**
 * Auth gate — coordinates auth state → which surface to render.
 *
 * Wraps the intake UI behind a Firebase Auth check. While the auth
 * client is resolving the initial session we render a quiet loading
 * card. Once resolved:
 *
 *   - signed-out + authSurface='sign-up' →
 *       AuthLayout + eligibility chip + journey panel + SignInForm(sign-up)
 *   - signed-out + authSurface='sign-in' →
 *       AuthLayout (centered, no chip, no panel) + SignInForm(sign-in)
 *   - unverified  → AuthLayout + EmailVerificationPrompt + verify panel
 *   - verified    → children (the intake)
 *
 * The two signed-out surfaces are intentionally different:
 *   - sign-up users are coming from the screener-eligible outcome and
 *     need framing for what's next + acknowledgment of their progress
 *   - sign-in users have already been here; they want the form to be
 *     the hero with no scaffolding
 */
import { type ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AuthClient } from '@inside-us-aos/firebase-adapter';
import type { User } from 'firebase/auth';

import { AuthLayout, EligibilityBadge } from './AuthLayout';
import { EmailVerificationPrompt } from './EmailVerificationPrompt';
import { SignInForm } from './SignInForm';
import { WhatsNextPanel } from './WhatsNextPanel';
import resolvingStyles from './AuthResolving.module.css';

export interface IAuthGateProps {
  authClient: AuthClient;
  /** Rendered only when a verified user is present. */
  children: (user: User) => ReactNode;
  /** Optional — opens a privacy page in a new section. */
  onShowPrivacy?: () => void;
}

type GateState =
  | { kind: 'resolving' }
  | { kind: 'signed-out' }
  | { kind: 'unverified'; user: User }
  | { kind: 'signed-in'; user: User };

export function AuthGate({
  authClient,
  children,
  onShowPrivacy,
}: IAuthGateProps): JSX.Element {
  const [state, setState] = useState<GateState>({ kind: 'resolving' });
  const navigate = useNavigate();
  const location = useLocation();
  // URL → signed-out surface. `/sign-in` shows the returning-user
  // surface; every other path shows the screener-handoff sign-up
  // surface (including `/sign-up` and `/intake` for users who tried
  // to enter intake before signing in).
  const authSurface: 'sign-up' | 'sign-in' =
    location.pathname === '/sign-in' ? 'sign-in' : 'sign-up';

  useEffect(() => {
    return authClient.subscribe((user) => {
      if (user === null) {
        setState({ kind: 'signed-out' });
        return;
      }
      // Google sign-in is implicitly verified; firebase already sets
      // `user.emailVerified` to true for Google so this simple check
      // works for both email/password and Google paths.
      if (!user.emailVerified) {
        setState({ kind: 'unverified', user });
        return;
      }
      setState({ kind: 'signed-in', user });
    });
  }, [authClient]);

  if (state.kind === 'resolving') {
    return (
      <AuthLayout onShowPrivacy={onShowPrivacy}>
        <div className={resolvingStyles.block}>
          <div className={resolvingStyles.spinner} aria-hidden="true" />
          <p className={resolvingStyles.text}>Checking your session…</p>
        </div>
      </AuthLayout>
    );
  }

  if (state.kind === 'signed-out') {
    if (authSurface === 'sign-in') {
      // Returning-user surface: form is the hero. No eligibility chip,
      // no journey panel — they've already been through the rest.
      return (
        <AuthLayout
          centered
          headerLink={{
            label: 'New here? Create account',
            onClick: () => navigate('/sign-up'),
          }}
          onShowPrivacy={onShowPrivacy}
        >
          <SignInForm
            authClient={authClient}
            initialMode="sign-in"
            onModeChange={() => navigate('/sign-up')}
          />
        </AuthLayout>
      );
    }
    // Sign-up surface: closes the loop on the screener-eligible
    // outcome and frames what's next.
    return (
      <AuthLayout
        eligibilityBadge={
          <EligibilityBadge label="Eligible for marriage-based AOS" />
        }
        sidePanel={<WhatsNextPanel phase="sign_up" />}
        headerLink={{
          label: 'Already have an account? Sign in',
          onClick: () => navigate('/sign-in'),
        }}
        onShowPrivacy={onShowPrivacy}
      >
        <SignInForm
          authClient={authClient}
          initialMode="sign-up"
          onModeChange={() => navigate('/sign-in')}
        />
      </AuthLayout>
    );
  }

  if (state.kind === 'unverified') {
    return (
      <AuthLayout
        sidePanel={<WhatsNextPanel phase="verify" />}
        onShowPrivacy={onShowPrivacy}
      >
        <EmailVerificationPrompt
          authClient={authClient}
          user={state.user}
          onVerified={() =>
            setState({ kind: 'signed-in', user: state.user })
          }
        />
      </AuthLayout>
    );
  }

  return <>{children(state.user)}</>;
}
